// Copyright 2025 Linka Cloud  All rights reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { GoIf, GoIfElse, goIfElse } from '@linka-cloud/go';
import { ArrowBackIosOutlined } from '@mui/icons-material';
import { Button, Stack, Typography } from '@mui/material';
import { GetStaticProps, NextPage } from 'next';
import React from 'react';
import { EmailForm } from '../components/EmailForm';
import { Header } from '../components/Header';
import { Layout } from '../components/Layout';
import { PasswordForm } from '../components/PasswordForm';
import { Data, DefaultData, PasswordReset, PasswordResetTemplate } from '../openid';

export const getStaticProps: GetStaticProps<Data<PasswordReset>> = async () => {
  return {
    props: { ...DefaultData, data: PasswordResetTemplate },
  };
};

const ForgotPasswordPage: NextPage<Data<PasswordReset>> = ({ data }) => {
  return (
    <Layout>
      <Stack>
        <GoIf value="not .Done" data={data}>
          <GoIf value=".Reset" data={data}>
            <GoIf value="not .Setup" data={data}>
              <Header title="Change password" />
            </GoIf>
            <GoIf value=".Setup" data={data}>
              <Header title="Configure password" />
            </GoIf>
          </GoIf>
          <GoIf value="not .Reset" data={data}>
            <Header title="Forgot password ?" />
          </GoIf>
        </GoIf>
        <GoIf value=".Done" data={data}>
          <GoIf value="not .Reset" data={data}>
            <Header title="Email sent" />
          </GoIf>
          <GoIf value=".Reset" data={data}>
            <GoIf value="not .Setup" data={data}>
              <Header title="Password changed" />
            </GoIf>
            <GoIf value=".Setup" data={data}>
              <Header title="Password set" />
            </GoIf>
          </GoIf>
        </GoIf>
        <Stack direction="column">
          <GoIf value=".Done" data={data}>
            <GoIf value="not .Reset" data={data}>
              <Typography variant="body1">If the account exists, we've sent you an email.</Typography>
            </GoIf>
          </GoIf>
          <GoIf value="not .Done" data={data}>
            <GoIf value="not .Reset" data={data}>
              <Typography variant="body1">No worries, we will email you to reset your password.</Typography>
            </GoIf>
            <GoIf value=".Reset" data={data}>
              <Typography variant="body1">Please create a complicated password.</Typography>
            </GoIf>
          </GoIf>
          <GoIf value=".Done" data={data}>
            <GoIf value=".Reset" data={data}>
              <GoIf value="not .Setup" data={data}>
                <Typography variant="body1">Your password has been successfully changed.</Typography>
                <Typography variant="body1" color="text.secondary">
                  You can now log in with your new password.
                </Typography>
              </GoIf>
              <GoIf value=".Setup" data={data}>
                <Typography variant="body1">Your password has been successfully set.</Typography>
                <Typography variant="body1" color="text.secondary">
                  You can now log in with your password.
                </Typography>
              </GoIf>
            </GoIf>
            <GoIf value="not .Reset" data={data}>
              <Typography variant="body1" color="text.secondary">
                Please check your inbox and follow the instructions.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                If you don't receive an email, please check your spam folder.
              </Typography>
            </GoIf>
          </GoIf>
        </Stack>
        <GoIf value="not .Done" data={data}>
          <GoIf value=".Reset" data={data}>
            <PasswordForm
              submitLabel={goIfElse('.Setup', 'Setup Password', 'Change Password', data)}
              postUrl={data.PostURL}
              csrf={data.CSRF}
            />
          </GoIf>
          <GoIf value="not .Reset" data={data}>
            <EmailForm submitLabel="Reset Password" postUrl={data.PostURL} csrf={data.CSRF} />
          </GoIf>
        </GoIf>
        <Stack direction="row" justifyContent="center" alignItems="center">
          <Button component="a" href="/" startIcon={<ArrowBackIosOutlined />}>
            Go back to login
          </Button>
        </Stack>
      </Stack>
    </Layout>
  );
};

export default ForgotPasswordPage;
